import React, {useState} from 'react'
import {Helmet} from 'react-helmet'
import {useFormik} from 'formik'
import styled from 'styled-components'
import overlay_img from 'images/loginb2.webp'
import {LocalLink} from 'i18n/LocalLink'
import {AuthSignHeader} from 'i18n/AuthSignHeader'
import {useLocalizedNavigate} from 'i18n/useLocalizedNavigate'
import {useI18n} from 'i18n/I18nContext'
import {routes} from 'routes/routes'
import {DEFAULT_LOCALE, LOCALE_LABELS, SUPPORTED_LOCALES} from 'i18n/locales'
import {withLocalePath} from 'i18n/localePaths'
import {SITE_ORIGIN, DEFAULT_OG_IMAGE} from 'seo/siteMeta'
import {CreateEventData} from 'organization/Event/CreateEventData'
import {handleAddressChange} from 'organization/Event/Settings'
import {savePendingEventDraft} from 'organization/CreateEventOnboarding/pendingEventDraftStorage'
import {
  IconEventName,
  IconEventUrl,
  IconLocale,
  IconReady,
} from 'organization/CreateEventOnboarding/OnboardingStepIcons'

const STEPS = 4

function defaultLocaleForUi(
  appLocale: string,
): (typeof SUPPORTED_LOCALES)[number] {
  return SUPPORTED_LOCALES.includes(
    appLocale as (typeof SUPPORTED_LOCALES)[number],
  )
    ? (appLocale as (typeof SUPPORTED_LOCALES)[number])
    : DEFAULT_LOCALE
}

function slugifyFromName(name: string) {
  const s = name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '')
  return s.slice(0, 64) || 'my-event'
}

function StepIcon({stepIndex}: {stepIndex: number}) {
  if (stepIndex === 0) {
    return <IconEventName />
  }
  if (stepIndex === 1) {
    return <IconEventUrl />
  }
  if (stepIndex === 2) {
    return <IconLocale />
  }
  return <IconReady />
}

export function CreateEventOnboarding() {
  const {t, locale} = useI18n()
  const navigate = useLocalizedNavigate()
  const [step, setStep] = useState(0)

  const {handleChange, handleBlur, values, setFieldValue, setValues} =
    useFormik<CreateEventData>({
      initialValues: {
        name: '',
        slug: '',
        locale: defaultLocaleForUi(locale),
      },
      onSubmit: async () => {},
    })

  const newEventPath = withLocalePath(locale, routes.organization.newEvent)
  const newEventUrl = `${SITE_ORIGIN}${newEventPath}`

  const title = t('createEventWizard.pageTitle')
  const progressPct = ((step + 1) / STEPS) * 100

  const goNext = () => {
    if (step === 0) {
      const trimmed = values.name.trim()
      if (!trimmed) {
        return
      }
      const nextSlug = values.slug.trim() || slugifyFromName(values.name)
      setValues({...values, name: trimmed, slug: nextSlug})
      setStep(1)
      return
    }
    if (step === 1) {
      if (!values.slug.trim() || !/^[a-zA-Z0-9-]+$/i.test(values.slug)) {
        return
      }
      setFieldValue('slug', values.slug.trim())
      setStep(2)
      return
    }
    if (step === 2) {
      setStep(3)
    }
  }

  const goBack = () => {
    if (step > 0) {
      setStep((s) => s - 1)
    }
  }

  const finishWithLogin = (authPath: string) => {
    if (!values.name.trim() || !values.slug.trim()) {
      return
    }
    savePendingEventDraft({
      name: values.name.trim(),
      slug: values.slug.trim(),
      locale: values.locale || defaultLocaleForUi(locale),
    })
    navigate(authPath)
  }

  return (
    <>
      <Helmet>
        <title>{title}</title>
        <meta name="robots" content="noindex,follow" />
        <meta
          name="description"
          content={t('createEventWizard.metaDescription')}
        />
        <link rel="canonical" href={newEventUrl} />
        <meta property="og:title" content={title} />
        <meta property="og:url" content={newEventUrl} />
        <meta property="og:image" content={DEFAULT_OG_IMAGE} />
      </Helmet>
      <main className="body-area bd-theme-light">
        <section className="signin__area">
          <div className="sign__main-wrapper">
            <div className="sign__left">
              <AuthSignHeader
                logo={
                  <div className="sign__logo">
                    <LocalLink to={routes.index}>
                      <img
                        style={{width: '150px', height: 'auto'}}
                        className="logo-black"
                        src={process.env.PUBLIC_URL + '/logo-dark.svg'}
                        alt={t('logo.alt')}
                      />
                    </LocalLink>
                  </div>
                }
                navLinks={
                  <div className="sign__link custome_link">
                    <LocalLink
                      className="sign__link-text"
                      to={routes.organization.login}
                    >
                      {t('common.login')}
                    </LocalLink>
                    <LocalLink
                      className="sign__link-text"
                      to={routes.organization.register}
                    >
                      {t('common.register')}
                    </LocalLink>
                  </div>
                }
              />
              <div className="sign__center-wrapper mt-35 w-100">
                <div className="text-center w-100">
                  <div className="sign__wizard-icon">
                    <StepIcon stepIndex={step} />
                  </div>
                </div>

                <div
                  className="sign__wizard-progress"
                  role="status"
                  aria-label={t('createEventWizard.stepOf')
                    .replace('{{c}}', String(step + 1))
                    .replace('{{m}}', String(STEPS))}
                >
                  <div className="sign__wizard-track" aria-hidden>
                    <div
                      className="sign__wizard-fill"
                      style={{width: `${progressPct}%`}}
                    />
                  </div>
                  <span className="sign__wizard-step">
                    {t('createEventWizard.stepOf')
                      .replace('{{c}}', String(step + 1))
                      .replace('{{m}}', String(STEPS))}
                  </span>
                </div>

                <div className="body__card-wrapper text-start create-event-wizard">
                  {step === 0 && (
                    <>
                      <p className="sign__wizard-kicker">
                        {t('createEventWizard.kicker')}
                      </p>
                      <div className="sign__title-wrapper mb-20">
                        <h3 className="sign__title text-start">
                          {t('createEventWizard.step1Title')}
                        </h3>
                        <p className="text-start">
                          {t('createEventWizard.step1Text')}
                        </p>
                      </div>
                      <div className="create__input-wrapper">
                        <div className="singel__input-field mb-0">
                          <label
                            className="input__field-text"
                            htmlFor="onb-name"
                          >
                            {t('createEvent.nameLabel')}{' '}
                            <small>
                              <i>{t('createEvent.nameHint')}</i>
                            </small>
                          </label>
                          <input
                            type="text"
                            name="name"
                            id="onb-name"
                            autoFocus
                            placeholder={t('createEvent.namePlaceholder')}
                            value={values.name}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            maxLength={180}
                          />
                        </div>
                      </div>
                    </>
                  )}

                  {step === 1 && (
                    <>
                      <div className="sign__title-wrapper mb-20">
                        <h3 className="sign__title text-start">
                          {t('createEventWizard.step2Title')}
                        </h3>
                        <p className="text-start">
                          {t('createEventWizard.step2Text')}
                        </p>
                      </div>
                      <div className="create__input-wrapper">
                        <div className="singel__input-field mb-0">
                          <label
                            className="input__field-text"
                            htmlFor="onb-slug"
                          >
                            {t('createEvent.slugLabel')} <br />
                            <small>
                              <i>{t('createEvent.slugHint')}</i>
                            </small>
                          </label>
                          <SlugInputGroup>
                            <span>www.eventa.tv/</span>
                            <input
                              type="text"
                              name="slug"
                              id="onb-slug"
                              placeholder={t('createEvent.slugPlaceholder')}
                              value={values.slug}
                              onChange={(e) => {
                                handleAddressChange(e, handleChange)
                              }}
                              onBlur={handleBlur}
                              autoComplete="off"
                              spellCheck={false}
                              maxLength={80}
                            />
                          </SlugInputGroup>
                        </div>
                      </div>
                    </>
                  )}

                  {step === 2 && (
                    <>
                      <div className="sign__title-wrapper mb-20">
                        <h3 className="sign__title text-start">
                          {t('createEventWizard.step3Title')}
                        </h3>
                        <p className="text-start">
                          {t('createEventWizard.step3Text')}
                        </p>
                      </div>
                      <div className="create__input-wrapper">
                        <div className="singel__input-field mb-0">
                          <label
                            className="input__field-text"
                            htmlFor="onb-loc"
                          >
                            {t('createEvent.localeLabel')}
                          </label>
                          <select
                            name="locale"
                            id="onb-loc"
                            value={values.locale}
                            onChange={handleChange}
                            onBlur={handleBlur}
                          >
                            {SUPPORTED_LOCALES.map((loc) => (
                              <option key={loc} value={loc}>
                                {LOCALE_LABELS[loc]}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                    </>
                  )}

                  {step === 3 && (
                    <>
                      <div className="sign__title-wrapper mb-20">
                        <h3 className="sign__title text-start">
                          {t('createEventWizard.step4Title')}
                        </h3>
                        <p className="text-start">
                          {t('createEventWizard.step4Text')}
                        </p>
                      </div>
                      <div className="sign__wizard-review">
                        <div className="sign__wizard-reviewRow">
                          <span className="sign__wizard-reviewLabel">
                            {t('createEventWizard.reviewName')}
                          </span>
                          <span className="sign__wizard-reviewVal">
                            {values.name}
                          </span>
                        </div>
                        <div className="sign__wizard-reviewRow">
                          <span className="sign__wizard-reviewLabel">
                            {t('createEventWizard.reviewUrl')}
                          </span>
                          <span className="sign__wizard-reviewVal">
                            www.eventa.tv/{values.slug}
                          </span>
                        </div>
                        <div className="sign__wizard-reviewRow">
                          <span className="sign__wizard-reviewLabel">
                            {t('createEventWizard.reviewLanguage')}
                          </span>
                          <span className="sign__wizard-reviewVal">
                            {
                              LOCALE_LABELS[
                                defaultLocaleForUi(values.locale || locale)
                              ]
                            }
                          </span>
                        </div>
                      </div>
                    </>
                  )}
                </div>

                <div
                  className={
                    step === 0
                      ? 'sign__wizard-actions'
                      : 'sign__wizard-actions sign__wizard-actions--split'
                  }
                >
                  {step < 3 ? (
                    <>
                      {step > 0 && (
                        <button
                          type="button"
                          className="sign__wizard-back"
                          onClick={goBack}
                        >
                          {t('createEventWizard.back')}
                        </button>
                      )}
                      <button
                        type="button"
                        className={
                          step === 0
                            ? 'input__btn w-100 mb-0'
                            : 'input__btn w-100 mb-0 sign__wizard-next'
                        }
                        onClick={goNext}
                      >
                        {t('createEventWizard.next')}
                      </button>
                    </>
                  ) : (
                    <>
                      <button
                        type="button"
                        className="sign__wizard-back"
                        onClick={goBack}
                      >
                        {t('createEventWizard.back')}
                      </button>
                      <button
                        type="button"
                        className="input__btn w-100 mb-0 sign__wizard-next"
                        onClick={() =>
                          finishWithLogin(routes.organization.login)
                        }
                      >
                        {t('createEventWizard.signInCta')}
                      </button>
                    </>
                  )}
                </div>
              </div>
            </div>
            <div className="sign__right">
              <div
                className="sign__input-thumb include__bg w-img"
                style={{
                  backgroundImage: `url(${overlay_img})`,
                  backgroundPosition: 'center',
                  backgroundSize: 'contain',
                }}
              />
            </div>
          </div>
        </section>
      </main>
    </>
  )
}

const SlugInputGroup = styled.div`
  display: flex;

  & > span {
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 0 15px;
    border: 1px solid var(--clr-border-4);
    border-radius: 4px;
    border-right: none;
  }
`
